/**
 * Fingerprint do dispositivo: um hash dos sinais estáveis do navegador, mandado junto do login.
 *
 * Complementa o cookie `asgard_dispositivo`, e não o substitui. O cookie é a identidade primária
 * porque nunca colide — é um UUID sorteado no servidor. O fingerprint colide, e colide justamente
 * neste parque: cartório tem vinte máquinas com o mesmo modelo, a mesma imagem de Windows e a mesma
 * resolução. O que ela adiciona são os dois casos que o cookie não enxerga: o mesmo cookie
 * aparecendo em máquinas diferentes (cookie copiado) e a máquina que limpou os cookies e voltou
 * como dispositivo novo.
 *
 * Os sinais foram escolhidos por serem BARATOS e ESTÁVEIS, nesta ordem:
 *
 * - baratos: nada que force layout, leia fontes uma a uma ou peça permissão ao usuário. O custo
 *   total é um canvas de 240x60 desenhado uma vez, e o resto é leitura de propriedade;
 * - estáveis: nada que mude quando a pessoa redimensiona a janela, muda o zoom ou vira o celular.
 *   Por isso entra `screen.width`, e não `innerWidth`; e por isso a versão do navegador fica de
 *   fora — ela muda sozinha a cada atualização, e transformaria a mesma máquina em um dispositivo
 *   novo por mês.
 *
 * O que NÃO é: prova de identidade. Atualização de driver de vídeo muda o canvas; troca de
 * navegador muda tudo. Serve para medir população de dispositivos, não para autorizar.
 */
const FingerprintDispositivoBusiness = {

  /**
   * Desenha um canvas pequeno e devolve o dataURL.
   *
   * O resultado depende da GPU, do driver e da pilha de fontes do sistema — é o sinal com mais
   * poder de separação da coleta. Envolvido em try/catch porque canvas pode estar bloqueado
   * (`privacy.resistFingerprinting` do Firefox devolve dados uniformes ou lança), e nesse caso a
   * fingerprint simplesmente sai mais fraco em vez de o login quebrar.
   */
  _canvas(){
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 240;
      canvas.height = 60;
      const ctx = canvas.getContext('2d');
      if(!ctx) return '';

      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f6f6f6';
      ctx.fillRect(0, 0, 240, 60);
      ctx.fillStyle = '#123456';
      ctx.font = '14px "Arial"';
      ctx.fillText('Asgard çãõ — 1234567890', 4, 20);
      ctx.font = '11px "Times New Roman"';
      ctx.fillStyle = 'rgba(20, 120, 200, 0.7)';
      ctx.fillText('registro de imóveis', 6, 42);

      return canvas.toDataURL();
    } catch (e) {
      return '';
    }
  },

  /** Os sinais crus, na ordem em que entram no hash. Ordem fixa: hash é sensível a ela. */
  _sinais(){
    const nav = window.navigator || {};
    const tela = window.screen || {};

    return [
      nav.platform || '',
      nav.language || '',
      (nav.languages || []).join(','),
      nav.hardwareConcurrency || '',
      nav.deviceMemory || '',
      nav.maxTouchPoints || '',
      tela.width || '',
      tela.height || '',
      tela.colorDepth || '',
      window.devicePixelRatio || '',
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      new Date().getTimezoneOffset(),
      this._canvas(),
    ].join('|');
  },

  /**
   * SHA-256 dos sinais, em hexadecimal.
   *
   * Usa `crypto.subtle`, que só existe em contexto seguro (https ou localhost). Fora dele — e em
   * qualquer outra falha — devolve `null`, e o login segue sem fingerprint: é instrumentação, e
   * instrumentação que impede alguém de entrar no sistema é pior que instrumentação ausente.
   */
  async calcular(){
    try {
      if(!window.crypto || !window.crypto.subtle) return null;

      const dados = new TextEncoder().encode(this._sinais());
      const buffer = await window.crypto.subtle.digest('SHA-256', dados);

      return Array.from(new Uint8Array(buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (e) {
      return null;
    }
  },

};

export default FingerprintDispositivoBusiness;
